/**
 * Sección "Borrar mi cuenta" + modal de confirmación (Sprint 14 S14.5, R3 GDPR).
 *
 * Acción IRREVERSIBLE → confirmación inequívoca y deliberada:
 * - Usuario nativo (con contraseña): reauth escribiendo la contraseña.
 * - Usuario solo-OAuth (sin contraseña): escribir "BORRAR".
 * Tras éxito: el contexto deja el estado anónimo → redirigimos a /login con
 * un mensaje. Errores (contraseña incorrecta) se muestran inline sin cerrar
 * sesión.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { FormError } from "@/components/ui/FormError";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import { t, translateError } from "@/i18n/es";
import { ApiError } from "@/types/api";

export function DeleteAccountSection() {
  const { user, deleteAccount } = useAuth();
  const navigate = useNavigate();
  // Default a "nativo" (pide contraseña) si no sabemos: lo más seguro.
  const hasPassword = user?.has_password ?? true;

  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canConfirm = hasPassword
    ? password.length > 0
    : confirmWord.trim().toUpperCase() === t.profile.deleteConfirmWord;

  function openModal() {
    setErr(null);
    setPassword("");
    setConfirmWord("");
    setOpen(true);
  }

  async function onConfirm() {
    setErr(null);
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await deleteAccount(hasPassword ? { password } : { confirm: true });
      navigate("/login", {
        replace: true,
        state: { successMessage: t.profile.deleteSuccess },
      });
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setErr(code ? translateError(code) : t.profile.deleteError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="mt-2 border-t border-border-subtle pt-6">
      <h2 className="text-xs uppercase tracking-widest text-gray-mid">
        {t.profile.deleteTitle}
      </h2>
      <p className="mt-2 text-sm text-bone-muted">{t.profile.deleteIntro}</p>
      <button
        type="button"
        data-testid="profile-delete-button"
        onClick={openModal}
        className="mt-3 text-sm text-danger underline-offset-4 hover:underline"
      >
        {t.profile.deleteButton}
      </button>

      {open && (
        <div
          data-testid="profile-delete-modal"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/80 p-6"
        >
          <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-6 text-bone">
            <h3 className="text-lg font-medium">{t.profile.deleteModalTitle}</h3>
            <p className="mt-3 text-sm text-bone-muted">
              {t.profile.deleteModalBody}
            </p>

            <div className="mt-4 flex flex-col gap-3">
              <FormError message={err} />
              {hasPassword ? (
                <Input
                  label={t.profile.deletePasswordLabel}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="profile-delete-password-input"
                />
              ) : (
                <Input
                  label={t.profile.deleteConfirmWordLabel}
                  type="text"
                  autoCapitalize="characters"
                  value={confirmWord}
                  onChange={(e) => setConfirmWord(e.target.value)}
                  data-testid="profile-delete-confirm-input"
                />
              )}
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                data-testid="profile-delete-cancel"
                onClick={() => setOpen(false)}
                className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
              >
                {t.profile.deleteCancel}
              </button>
              <Button
                type="button"
                data-testid="profile-delete-confirm"
                loading={submitting}
                disabled={!canConfirm}
                onClick={onConfirm}
                className="bg-danger text-bone hover:bg-danger/90"
              >
                {t.profile.deleteConfirmCta}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
